// @core/stores/license/LicenseStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { licenseApi } from '@core/api';
import type {
  ActivateLicenseRequest,
  DeactivateLicenseRequest,
  GetLicenseAuditLogsResponse,
  GetPublicKeyResponse,
  GetUsageHistoryResponse,
  GetViolationsResponse,
  InputLicenseKeyRequest,
  IssueLicenseRequest,
  License,
  LicenseResponse,
  LicenseStatusResponse,
  RenewLicenseRequest,
  ReportUsageRequest,
  ReportUsageResponse,
  ResumeLicenseRequest,
  RevokeLicenseRequest,
  RotateSigningKeyRequest,
  RotateSigningKeyResponse,
  SuspendLicenseRequest,
  SyncLicenseRequest,
  SyncLicenseResponse,
  UpdateLicenseEntitlementsRequest,
} from '@core/types/license';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class LicenseStore {
  // List state
  items: License[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: License | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, License>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): License | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: License | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Activate license key
   */
  async ActivateLicense(data: ActivateLicenseRequest): Promise<LicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.ActivateLicense(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Deactivate license
   */
  async DeactivateLicense(license_id: string, data: DeactivateLicenseRequest): Promise<LicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.DeactivateLicense(license_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get license by ID
   */
  async GetLicense(license_id: string): Promise<License> {
    // Check cache first
    const cached = this.cache.get(license_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await licenseApi.GetLicense(license_id);
      const item = response.license;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get license audit logs
   */
  async GetLicenseAuditLogs(license_id: string): Promise<GetLicenseAuditLogsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.GetLicenseAuditLogs(license_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get current license status
   */
  async GetLicenseStatus(params?: { }): Promise<LicenseStatusResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.GetLicenseStatus(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get public signing key
   */
  async GetPublicKey(params?: { }): Promise<GetPublicKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.GetPublicKey(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get usage history
   */
  async GetUsageHistory(license_id: string): Promise<GetUsageHistoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.GetUsageHistory(license_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get license violations
   */
  async GetViolations(license_id: string): Promise<GetViolationsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.GetViolations(license_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Input license key
   */
  async InputLicenseKey(data: InputLicenseKeyRequest): Promise<LicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.InputLicenseKey(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Issue new license
   */
  async IssueLicense(data: IssueLicenseRequest): Promise<LicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.IssueLicense(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List licenses
   */
  async ListLicenses(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await licenseApi.ListLicenses(params);

      runInAction(() => {
        this.items = response.licenses ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Renew license
   */
  async RenewLicense(license_id: string, data: RenewLicenseRequest): Promise<LicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.RenewLicense(license_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Report usage snapshot
   */
  async ReportUsage(license_id: string, data: ReportUsageRequest): Promise<ReportUsageResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.ReportUsage(license_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resume license
   */
  async ResumeLicense(license_id: string, data: ResumeLicenseRequest): Promise<LicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.ResumeLicense(license_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke license
   */
  async RevokeLicense(license_id: string, data: RevokeLicenseRequest): Promise<LicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.RevokeLicense(license_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Rotate signing key
   */
  async RotateSigningKey(data: RotateSigningKeyRequest): Promise<RotateSigningKeyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.RotateSigningKey(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Suspend license
   */
  async SuspendLicense(license_id: string, data: SuspendLicenseRequest): Promise<LicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.SuspendLicense(license_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Force license sync
   */
  async SyncLicense(data: SyncLicenseRequest): Promise<SyncLicenseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await licenseApi.SyncLicense(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update license entitlements
   */
  async UpdateLicenseEntitlements(license_id: string, data: UpdateLicenseEntitlementsRequest): Promise<License> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await licenseApi.UpdateLicenseEntitlements(license_id, data);
      const item = response.license;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const licenseStore = new LicenseStore();
